// `modal.dialog.unified.tpl.html` + `modal.service.ng1.ts`, ported — the shell every
// shortlist dialogue is shown inside.
//
// ⚠️ ONLY THE BRANCHES THAT ARE USED, AND THE OMISSIONS ARE DELIBERATE. The upstream template
// carries a footer with action/cancel/footer-link buttons in two responsive variants, an
// over-all title, a subtitle and a `visual` indication block — and `showConfirmDialogue`
// passes NONE of them: it supplies `bodyTemplateUrl` alone, so every one of those `ng-if`s is
// false and the rendered DOM is the header's close button and the body. Porting the unused
// branches would be markup no call site can reach, and each would need its own pixel
// reference to be worth anything.
//
// ⚠️ `dialog-unified`, NOT `dialog-common`. `show()` rewrites the window class when
// `useUnifiedTemplate` is set, and the two have different stylesheets — the shortlist
// dialogues all pass it.
import { useEffect, useRef, type ReactElement, type ReactNode } from 'react'
import { useModalLayer } from './modalStack.ts'

export interface UnifiedModalProps {
  open: boolean
  /**
   * `windowClass` as the caller passes it to `modalService.show()`, e.g. `'dialog-unified
   * shortlist-dialogue change-state-dialogue-container'`.
   *
   * ⚠️ IT REPLACES THE DEFAULT, IT IS NOT APPENDED TO IT. `show()` is `angular.extend(defaults,
   * custom)`, so a caller's `windowClass` overwrites `'dialog-base dialog-common'` whole; the
   * `dialog-common` → `dialog-unified` rewrite only ever touches the default. Every shortlist
   * dialogue passes one, so none of them has `dialog-base` — measured on the dev tenant, 28 Oct.
   */
  windowClass?: string
  /** `ctrl.allowClose === false` hides the header's X. */
  allowClose?: boolean
  onClose: () => void
  /**
   * `modalScope.$on('modal.closing', (event, action) => …)` — consulted before each of the three
   * dismissals, with uib's own action string; answer `false` for `event.preventDefault()`.
   * Absent, all three close, which is every dialogue that registers no listener.
   */
  onClosing?: (action: ModalCloseAction) => boolean
  children: ReactNode
}

/**
 * The `action` uib hands `modal.closing` for each way out this shell offers: the header's ✕ is
 * `dismiss('close')` (`modal.service.ng1.ts`), and the other two are `$uibModalStack`'s own.
 */
export type ModalCloseAction = 'close' | 'backdrop click' | 'escape key press'

/**
 * ⚠️ THE ELEMENT CHAIN IS `$uibModal`'s, NOT A WRAPPER OF OUR OWN — and this port had a
 * native `<dialog>` here instead, which is what made the confirmation look wrong. Every rule
 * that gives the dialogue its shape hangs off a class ui-bootstrap renders and the template
 * never mentions: `.change-state-dialogue-container .modal-dialog` is where the 351px width
 * and the 228px min-height live, `.shortlist-dialogue .modal-content` is where the radius and
 * the shadow live, and `.modal-backdrop.in` IS a separate element, a SIBLING of the window
 * rather than a child. With none of them present the browser's own `<dialog>` defaults showed
 * through: a 2px black border, a box centred in the viewport instead of 30px from its top,
 * and a `::backdrop` that barely tints the page where production dims it by half.
 *
 * So this renders what ui-bootstrap renders — `.modal-backdrop.fade.in` then
 * `.modal.fade.in > .modal-dialog > .modal-content` — and the ported stylesheets land on it
 * unchanged. `ShortlistDialogue` already reproduced this chain for the delete dialogue, for
 * exactly the same reason; the two now agree.
 *
 * What the `<dialog>` element was giving us and a div does not is Escape and the focus trap.
 * Escape is restored below, on the same `keyboard: true` default `$uibModal` has. The focus
 * trap is not reproduced: upstream has none either (ui-bootstrap only moves focus into the
 * window), so adding one would be a behaviour this screen is not diffed against.
 */
export function UnifiedModal({
  open,
  windowClass,
  allowClose = true,
  onClose,
  onClosing,
  children,
}: UnifiedModalProps): ReactElement | null {
  const windowRef = useRef<HTMLDivElement>(null)
  const layer = useModalLayer(open)

  /**
   * ui-bootstrap's focus move on open: the first `[autofocus]` inside the window, else the window
   * itself. Without it the control that OPENED the dialogue keeps focus behind the backdrop and
   * draws its focus ring there — measured on the edit screen's Cancel link, 28 Oct.
   */
  useEffect(() => {
    if (!open) return
    const win = windowRef.current
    if (win === null) return
    // React's `autoFocus` focuses the element itself and writes no attribute, so a field that
    // asked for focus already has it by now; taking it back to the window would undo that.
    if (win.contains(document.activeElement)) return
    const target = win.querySelector<HTMLElement>('[autofocus]') ?? win
    target.focus()
  }, [open])

  useEffect(() => {
    if (!open || !allowClose) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      if (onClosing?.('escape key press') === false) return
      onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, allowClose, onClose, onClosing])

  if (!open) return null

  return (
    <>
      {/* `backdrop: true` — `modalBackdrop` is appended beside the window, at z-index 1040.
          Note that `.dialog-unified .modal-backdrop.in { opacity: 0.4 }` in `_theme.scss`
          never matches anything, here or upstream: the backdrop is the window's sibling, not
          its descendant. The 0.5 bootstrap gives it is what production actually shows. */}
      {layer.backdropZIndex === undefined ? null : (
        <div className="modal-backdrop fade in" style={{ zIndex: layer.backdropZIndex }} />
      )}
      <div
        ref={windowRef}
        className={`modal fade ${windowClass ?? 'dialog-base dialog-unified'} in`}
        role="dialog"
        tabIndex={-1}
        // ui-bootstrap's `ng-style` on the window element; `.modal` is `display: none` in
        // bootstrap and the service is what turns it on.
        style={{ zIndex: layer.windowZIndex, display: 'block' }}
        onClick={(event) => {
          // `backdrop: true` (not `'static'`) — a click on the window outside the dialogue
          // dismisses it. The target test is upstream's: the window element itself, never a
          // child.
          if (!allowClose || event.target !== event.currentTarget) return
          if (onClosing?.('backdrop click') === false) return
          onClose()
        }}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-content-inner">
              <div className="modal-header">
                {allowClose ? (
                  <button
                    type="button"
                    className="close"
                    onClick={() => {
                      if (onClosing?.('close') === false) return
                      onClose()
                    }}
                  >
                    <span aria-hidden="true">&times;</span>
                    <span className="sr-only">close</span>
                  </button>
                ) : null}
              </div>
              <div className="modal-body">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
